// localStorage.js
import { isVNR } from './FobiCore';

/**
 * Nimmt E-Mail-Adressen aus den gespeicherten Beschriftungen (September 2026).
 *
 * Eine Beschriftung besteht nur noch aus Text. Die Adressen einfach nicht mehr anzuzeigen, hiesse:
 * personenbezogene Daten bleiben im localStorage liegen, unsichtbar und ueber die Oberflaeche
 * nicht mehr loeschbar - schlechter als der Zustand davor.
 *
 * Leere Reste werden dabei aufgeraeumt, sonst entstuenden Eintraege, die der Reducer selbst nie
 * anlegen wuerde: eine Beschriftung ohne Text ist keine, und ein abweichender Scan ohne Text
 * widerspricht nichts. Traegt nur der Konflikt einen Namen, wird er zur Beschriftung - sonst
 * waere der einzige vorhandene Name unsichtbar.
 */
export const ohneEmails = (state) => {
  let geaendert = false;
  const beschriftungen = state && state.punkteMeldung && state.punkteMeldung.beschriftungen;
  if(!beschriftungen) return { state, geaendert };
  const bereinigt = {};
  Object.entries(beschriftungen).forEach(([vnr, jeEFN]) => {
    const neueJeEFN = {};
    Object.entries(jeEFN || {}).forEach(([efn, alt]) => {
      if(!alt || typeof alt !== 'object') return;
      if('email' in alt) geaendert = true;
      // Auch die verschachtelte Adresse zaehlt: sonst bliebe { text: "Max", abweichenderScan:
      // { text: "Erika", email: "..." } } unangetastet, weil oben kein email steht - das intern
      // bereinigte Objekt wuerde wegen !geaendert gar nicht erst zurueckgegeben.
      if(alt.abweichenderScan && 'email' in alt.abweichenderScan) geaendert = true;
      const scanText = ((alt.abweichenderScan && alt.abweichenderScan.text) || '').trim();
      if(alt.abweichenderScan && !scanText) geaendert = true;
      let text = (alt.text || '').trim();
      // Nur der Konflikt traegt einen Namen: er wird zur Beschriftung, und damit ist er keiner
      // mehr - es gibt nichts, dem er widersprechen koennte.
      if(!text && scanText){ text = scanText; geaendert = true; }
      else if(scanText) neueJeEFN[efn] = { text, abweichenderScan: { text: scanText } };
      if(!text){ if(alt.text !== undefined || 'email' in alt) geaendert = true; return; }
      if(!neueJeEFN[efn]) neueJeEFN[efn] = { text };
    });
    bereinigt[vnr] = neueJeEFN;
  });
  if(!geaendert) return { state, geaendert };
  return { state: { ...state, punkteMeldung: { ...state.punkteMeldung, beschriftungen: bereinigt } },
    geaendert };
};

export const loadState = () => {
  try {
		const serializedState = localStorage.getItem('state');
		if (serializedState === null) {
      return undefined;
    }
    const geladen = JSON.parse(serializedState);
    const { state, geaendert } = ohneEmails(geladen);
    // **Sofort zurueckschreiben**, nicht auf den naechsten gedrosselten saveState warten: ein
    // Geraet, das nur geoeffnet und wieder geschlossen wird, loest den unter Umstaenden nie aus,
    // und die Adresse laege weiter da. Bewusst unabhaengig von saveStateToLocalStorage - das ist
    // keine neue Speicherung, sondern das Bereinigen von Daten, die schon dort liegen; saveState
    // nimmt ausschliesslich weg.
    if(geaendert) saveState(state);
    return state;
  } catch (err) {
    return undefined;
  }
};

/**
 * Schreibt den Zustand in den localStorage und meldet, **ob** das gelungen ist.
 *
 * Der Rueckgabewert haengt an einer Zusage in der Oberflaeche: "Auf diesem Geraet gespeichert"
 * darf erst dastehen, wenn wirklich geschrieben wurde. Ein blockierter oder voller Speicher
 * (privates Fenster, "Cookies blockieren", Quota) ist real und darf nicht als Erfolg
 * durchgehen - vorher wurde der Fehler nur geloggt. Der subscribe-Aufruf in index.js ignoriert
 * den Wert weiterhin, dort gibt es niemanden, dem man etwas sagen koennte.
 */
export const saveState = (state) => {
  try {
    const cleanState = JSON.parse(JSON.stringify(state));
    if( cleanState.punkteMeldung.teilnahmeMeldungen) delete cleanState.punkteMeldung.teilnahmeMeldungen;
    cleanState.fetchCounter = 0;
    // Laufende Vorgaenge ueberleben keinen Reload - sonst kaeme ein Tab, der mitten in der
    // Meldeschleife geschlossen wurde, mit dauerhaft gesperrtem Meldeknopf zurueck.
    Object.values(cleanState.punkteMeldung.meldeversuch || {}).forEach(v => delete v.laeuft);
    // Zugangseingaben gehoeren nicht in den Speicher. Im Hinzufuegen-Formular steht das
    // VNR-Kennwort, und solange der Tausch gegen den Bearer nicht gelaufen ist, unter Umstaenden
    // ein eingefuegter Einrichtungstoken - beides in vnr oder vnrpwd. Der Hinweis neben dem
    // Speichern-Knopf verspricht Veranstaltung, Teilnahmen und Namen, sonst nichts.
    //
    // Das macht die Ablage **nicht** zugangsdatenfrei: der bearer je Veranstaltung bleibt, ohne
    // ihn ist die App nach einem Reload tot. Weg ist das Kennwort, und das ist der Unterschied,
    // auf den es ankommt - aus ihm lassen sich beliebig neue Tokens holen, es ueberlebt jede
    // Token-Entwertung und wird in der Praxis mehrfach verwendet.
    if(cleanState.addVeranstaltungPanel){
      cleanState.addVeranstaltungPanel.vnrpwd = "";
      // Eine gueltige VNR ist keine Zugangsangabe und darf bleiben - sie kommt oft aus dem
      // Portal der Kammer. Alles andere in diesem Feld ist unfertige Eingabe oder ein Token.
      if(!isVNR(cleanState.addVeranstaltungPanel.vnr)) cleanState.addVeranstaltungPanel.vnr = "";
    }
    const serializedState = JSON.stringify(cleanState);
    localStorage.setItem('state', serializedState);
    return true;
  } catch(err) {
    console.error(err);
    return false;
  }
};
